"use client";
import React, { useState, useEffect, use } from 'react';

interface Picture {
    data: {
        id: string;
        attributes: {
            url: string;
            name: string;
            alternativeText: string;
        };
    };
}
interface Button {
    id?: string;
    url: string;
    text: string;
}
interface PodcastPageProps {
    data: {
        breadCrumbs: Button[];
        podcastVideo: {
            uniqueId: string;
            podcastTitle: string;
            podcastDescription: string;
            episode: string;
            detailedDescription: string;
            videoUrl: string;
            thumbnail: Picture;
        }[]
    }
}

const getYouTubeEmbeddedUrl = (url: string) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    const videoId = (match && match[2].length === 11) ? match[2] : null;
    return `https://www.youtube.com/embed/${videoId}`;
};
const PodcastPage = ({ data }: PodcastPageProps) => {
    const [selectedVideo, setSelectedVideo] = useState(0);
    const [breadCrumbs, setBreadCrumbs] = useState<Button[]>(data.breadCrumbs || []);

    const videos = data.podcastVideo

    const currentVideo = videos[selectedVideo];


    // Set initial video based on URL parameter
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const videoId = urlParams.get('videoId');

        if (videoId) {
            const videoIndex = videos.findIndex(v => v.uniqueId === videoId);
            if (videoIndex !== -1) {
                setSelectedVideo(videoIndex);
            } else {
                setSelectedVideo(0);
            }
        }


    }, []);

    useEffect(() => {
        const updatedCrumbs = [...data.breadCrumbs];

        // Check if a podcast crumb already exists
        const existingIndex = updatedCrumbs.findIndex(b => b.id === 'podcast');

        const newCrumb: Button = {
            id: 'podcast',
            url: `/podcasts?videoId=${currentVideo.uniqueId}`,
            text: currentVideo.podcastTitle
        };

        if (existingIndex !== -1) {
            // Replace existing podcast breadcrumb
            updatedCrumbs[existingIndex] = newCrumb;
        } else {
            // Add it as the last breadcrumb
            updatedCrumbs.push(newCrumb);
        }

        setBreadCrumbs(updatedCrumbs);
    }, [selectedVideo, data.breadCrumbs]);



    return (
        <div className="min-h-screen bg-gray-50">

            <div className="max-w-6xl mx-auto py-8 max-md:px-4">
                {/* Breadcrumb Navigation */}
                <Breadcrumb items={breadCrumbs} />

                {/* Main Content Area */}
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 pb-8 md:pb-12 lg:pb-12 ">
                    {/* Main Video Section */}
                    <div className="lg:col-span-3">
                        {/* Video Player */}
                        <div className="overflow-hidden aspect-video group">
                            <div className="relative w-full pb-[56.25%] ">
                                <iframe
                                    src={getYouTubeEmbeddedUrl(currentVideo.videoUrl) + '?autoplay=1&mute=1'}
                                    className="absolute top-0 left-0 w-full h-full rounded-xl shadow-md"
                                    referrerPolicy="strict-origin-when-cross-origin"
                                    allowFullScreen
                                />
                            </div>
                        </div>
                    </div>

                    {/* Sidebar with Related Videos */}
                    <div className="lg:col-span-2">
                        <div className="space-y-4 max-h-96 overflow-y-auto scrollbar-hide">
                            {videos.map((video, index) => (
                                <div
                                    key={video.uniqueId}
                                    onClick={() => setSelectedVideo(index)}
                                    className="flex cursor-pointer transition-all hover:bg-gray-50 border border-transparent items-stretch"
                                >
                                    {/* Image section (50%) */}
                                    <div className="flex-1 flex items-center justify-center p-2">
                                        <img
                                            src={video.thumbnail.data.attributes.url}
                                            className="w-full h-auto object-contain"
                                        />
                                    </div>

                                    {/* Text section (50%) */}
                                    <div className="flex-1 flex flex-col justify-center p-4">
                                        <h4 className="text-md text-[#032D60] font-bold mb-1">
                                            {video.podcastTitle}
                                        </h4>
                                        <p className="text-md text-[#032D60] line-clamp-3">
                                            {video.podcastDescription}
                                        </p>
                                    </div>
                                </div>


                            ))}
                        </div>
                    </div>
                </div>

                {/* Video Title and Episode */}
                <div className="mb-6">
                    <h1 className="text-4xl font-bold text-[#0095EB] mb-2">
                        {currentVideo.episode} : {currentVideo.podcastTitle}
                    </h1>
                </div>

                {/* Detailed Description */}
                <div className="text-[#032D60] text-xl " dangerouslySetInnerHTML={{ __html: currentVideo.detailedDescription }} />
            </div>
        </div>
    );
};

export default PodcastPage;

const Breadcrumb = ({ items }: any) => {
    return (
        <div className="flex justify-center pb-8">
            <div className="max-w-6xl w-full mx-4" style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                <div className="flex lg text-left">
                    {items?.map((item: Button, index: number) => (
                        <li key={item.id} className="flex items-center">
                            <a
                                href={item.url}
                                className="font-medium max-sm:text-xl max-sm:whitespace-nowrap md:text-[20px] md:leading-[45px] tracking-[0] text-[#032d60] text-left max-sm:text-center hover:text-blue-600"
                            >
                                {item.text}
                            </a>
                            {index < items.length - 1 && (
                                <svg className="rtl:rotate-180 w-4 h-4 text-blue-400 mx-1" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 6 10">
                                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 9 4-4-4-4" />
                                </svg>
                            )}
                        </li>
                    ))}
                </div>
            </div>
        </div>
    );
};